/**
 * @file AI Gallery「发布为网页」— UseFormSearch 字段配置（与 SmartFillForm / SaveReportModal 同模式）。
 * 
 * API 字段规范：
 * - name_cn: string(1-40) 作品中文名（必填）
 * - name_en: string(1-60) 作品英文名（必填）
 * - desc_cn: string(0-200) 中文描述
 * - desc_en: string(0-300) 英文描述
 * - tags: TagRef[] 1-5 个标签，元素 {id: 1..9}（必填）
 * - visibility: enum (self / user / public)（必填）
 * - share_users: string[] visibility=user 时至少 1 个 openid
 */
import React, { useMemo } from 'react';
import { SearchBox, TextArea } from 'tea-component';
import { useIntl } from 'umi';
import type { IConfig } from '@gp/pages/Pipeline/ProjectDetails/components/GameOverviewEdit/Components/UseFormSearch';
import { normalizeAiGalleryPublishVisibilityPayload } from '../components/AiGalleryPublishModal/constants';
import {
  AiGalleryPublishVisibilityField,
  AiGalleryPublishTagsField,
  AiGalleryPublishDataModeField,
} from '../components/AiGalleryPublishModal/AiGalleryPublishFormControls';
import type { IAiGalleryTagItem, IAiGalleryUserItem } from './useAiGalleryPublishApi';

/** 表单配置 Hook 参数 */
export interface IUseAiGalleryPublishFormConfigOptions {
  /** 标签选项列表（从 API 获取） */
  tagOptions?: IAiGalleryTagItem[];
  /** 标签加载中 */
  tagsLoading?: boolean;
  /** 创建自定义标签回调 */
  onCreateTag?: (params: { name_cn: string; name_en: string; }) => Promise<number | null>;
  /** 创建标签加载中 */
  createTagLoading?: boolean;
  /** 用户搜索回调 */
  onSearchUsers?: (keyword: string) => Promise<IAiGalleryUserItem[]>;
  /** 用户搜索加载中 */
  searchLoading?: boolean;
}

/**
 * AI Gallery 发布表单配置 Hook
 */
export function useAiGalleryPublishFormConfig(options: IUseAiGalleryPublishFormConfigOptions = {}): {
  config: IConfig[];
} {
  const { formatMessage } = useIntl();
  const { tagOptions, tagsLoading, onCreateTag, createTagLoading, onSearchUsers, searchLoading } = options;

  const config = useMemo((): IConfig[] => {
    const nameCnRequiredMsg = formatMessage({
      id: 'dba_ai_gallery_publish_name_cn_required',
    });
    const nameEnRequiredMsg = formatMessage({
      id: 'dba_ai_gallery_publish_name_en_required',
    });
    const shareUsersRequiredMsg = formatMessage({
      id: 'dba_ai_gallery_publish_share_users_required',
    });
    
    // 最大长度提示文案
    const nameCnMaxLengthTip = formatMessage({ id: 'dba_ai_gallery_publish_name_cn_max_length_tip' });
    const nameEnMaxLengthTip = formatMessage({ id: 'dba_ai_gallery_publish_name_en_max_length_tip' });
    const descCnMaxLengthTip = formatMessage({ id: 'dba_ai_gallery_publish_desc_cn_max_length_tip' });
    const descEnMaxLengthTip = formatMessage({ id: 'dba_ai_gallery_publish_desc_en_max_length_tip' });

    return [
      // 作品中文名（必填，最大40字）
      {
        name: 'nameCn',
        label: (
          <span>
            {formatMessage({ id: 'dba_ai_gallery_publish_name_cn' })}
          </span>
        ),
        component: (
          <SearchBox
            placeholder={formatMessage({
              id: 'dba_ai_gallery_publish_name_cn_placeholder',
            })}
          />
        ),
        rules: {
          required: true,
          message: nameCnRequiredMsg,
          validator: (values: Record<string, unknown>) => {
            const nameCn = String(values.nameCn ?? '').trim();
            if (!nameCn) return nameCnRequiredMsg;
            if (nameCn.length > 40) return nameCnMaxLengthTip;
            return '';
          },
        },
      },
      
      // 作品英文名（必填，最大60字）
      {
        name: 'nameEn',
        label: (
          <span>
            {formatMessage({ id: 'dba_ai_gallery_publish_name_en' })}
          </span>
        ),
        component: (
          <SearchBox
            placeholder={formatMessage({
              id: 'dba_ai_gallery_publish_name_en_placeholder',
            })}
          />
        ),
        rules: {
          required: true,
          message: nameEnRequiredMsg,
          validator: (values: Record<string, unknown>) => {
            const nameEn = String(values.nameEn ?? '').trim();
            if (!nameEn) return nameEnRequiredMsg;
            if (nameEn.length > 60) return nameEnMaxLengthTip;
            return '';
          },
        },
      },
      
      // 中文描述（可选，最大200字）
      {
        name: 'descCn',
        label: formatMessage({ id: 'dba_ai_gallery_publish_desc_cn' }),
        component: (
          <TextArea
            size="full"
            rows={4}
            placeholder={formatMessage({
              id: 'dba_ai_gallery_publish_desc_cn_placeholder',
            })}
          />
        ),
        rules: {
          required: false,
          message: '',
          validator: (values: Record<string, unknown>) => {
            const descCn = String(values.descCn ?? '');
            if (descCn.length > 200) return descCnMaxLengthTip;
            return '';
          },
        },
      },
      
      // 英文描述（可选，最大300字）
      {
        name: 'descEn',
        label: formatMessage({ id: 'dba_ai_gallery_publish_desc_en' }),
        component: (
          <TextArea
            size="full"
            rows={4}
            placeholder={formatMessage({
              id: 'dba_ai_gallery_publish_desc_en_placeholder',
            })}
          />
        ),
        rules: {
          required: false,
          message: '',
          validator: (values: Record<string, unknown>) => {
            const descEn = String(values.descEn ?? '');
            if (descEn.length > 300) return descEnMaxLengthTip;
            return '';
          },
        },
      },
      
      // 数据类型（真实数据 / Demo 数据）
      {
        name: 'dataMode',
        label: (
          <span>
            {formatMessage({ id: 'dba_ai_gallery_publish_data_mode' })}
          </span>
        ),
        component: <AiGalleryPublishDataModeField />,
        rules: {
          required: true,
          message: formatMessage({ id: 'dba_ai_gallery_publish_data_mode_required' }),
        },
      },
      
      // 标签选择器（1-5 个标签，必填并展示红色必填标识）
      {
        name: 'tags',
        label: formatMessage({ id: 'dba_ai_gallery_publish_tags' }),
        component: (
          <AiGalleryPublishTagsField
            tagOptions={tagOptions}
            tagsLoading={tagsLoading}
            onCreateTag={onCreateTag}
            createTagLoading={createTagLoading}
          />
        ),
        rules: {
          required: true,
          message: formatMessage({ id: 'dba_ai_gallery_publish_tags_required' }),
          validator: (values: Record<string, unknown>) => {
            const tags = values.tags as number[] | undefined;
            if (!tags || tags.length === 0) {
              return formatMessage({ id: 'dba_ai_gallery_publish_tags_required' });
            }
            return '';
          },
        },
      },
      
      // 访问权限选择器（必填）
      {
        name: 'visibilityPayload',
        label: (
          <span>
            {formatMessage({ id: 'dba_ai_gallery_publish_visibility' })}
          </span>
        ),
        component: (
          <AiGalleryPublishVisibilityField
            onSearchUsers={onSearchUsers}
            searchLoading={searchLoading}
          />
        ),
        rules: {
          required: true,
          message: shareUsersRequiredMsg,
          validator: (values: Record<string, unknown>) => {
            const payload = normalizeAiGalleryPublishVisibilityPayload(
              values.visibilityPayload,
            );
            // visibility=user 时至少需要 1 个用户
            if (
              payload.visibility === 'user' &&
              (!payload.shareUsers || payload.shareUsers.length === 0)
            ) {
              return shareUsersRequiredMsg;
            }
            return '';
          },
        },
      },
    ];
  }, [formatMessage, tagOptions, tagsLoading, onCreateTag, createTagLoading, onSearchUsers, searchLoading]);

  return { config };
}
